import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { ArrowLeft, Bell, BellOff, Volume2, VolumeX, Users, MessageSquare, Play } from 'lucide-react';
import { toast } from 'sonner';
import { playNotificationSound } from '../utils/notificationSounds';
import { loadNotificationSettings } from '../utils/notificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationSettings = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    message_sound: true,
    message_vibrate: true,
    show_preview: true,
    group_notifications: true,
    mention_notifications: true,
    ringtone: 'default'
  });
  const [mutedChats, setMutedChats] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  const ringtones = [
    { id: 'default', name: 'Default' },
    { id: 'chime', name: 'Chime' },
    { id: 'bell', name: 'Bell' },
    { id: 'ping', name: 'Ping' },
    { id: 'pop', name: 'Pop' },
    { id: 'none', name: 'None (Silent)' }
  ];

  useEffect(() => {
    fetchSettings();
    fetchChats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/users/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setMutedChats(response.data.muted_chats || []);
    } catch (error) {
      // Use defaults if no settings saved yet
      console.log('Using default notification settings');
    }
    setLoading(false);
  };

  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API}/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/users/notification-settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(newSettings);
      // Update the in-memory notification manager so changes take effect immediately
      loadNotificationSettings(newSettings);
      // Also persist to localStorage for cross-tab/reload consistency
      localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const toggleSetting = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    updateSettings(newSettings);
  };

  const changeRingtone = (ringtone) => {
    const newSettings = { ...settings, ringtone };
    updateSettings(newSettings);
  };

  const toggleMuteChat = async (chatId) => {
    const isMuted = mutedChats.includes(chatId);
    let newMutedChats;
    
    if (isMuted) {
      newMutedChats = mutedChats.filter(id => id !== chatId);
    } else {
      newMutedChats = [...mutedChats, chatId];
    }
    
    try {
      await axios.put(`${API}/users/notification-settings`, {
        ...settings,
        muted_chats: newMutedChats
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMutedChats(newMutedChats);
      toast.success(isMuted ? 'Chat unmuted' : 'Chat muted');
    } catch (error) {
      toast.error('Failed to update mute settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-DEFAULT flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-950' : 'bg-background-DEFAULT'}`} data-testid="notification-settings-page">
      <div className={`p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
        <div className="chat-container flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/settings')}
            className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-secondary-DEFAULT'}`}
          >
            <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-text-primary'} />
          </button>
          <h1 className={`font-heading text-2xl ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Notifications</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto space-y-6">
          {/* General Notifications */}
          <div className={`rounded-3xl p-6 shadow-card border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'}`}>
            <h2 className={`font-heading text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>General</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell size={20} className="text-primary" />
                  <div>
                    <p className="font-medium text-text-primary">Enable Notifications</p>
                    <p className="text-sm text-text-secondary">Receive push notifications</p>
                  </div>
                </div>
                <button
                  data-testid="toggle-notifications"
                  onClick={() => toggleSetting('notifications_enabled')}
                  className={`w-12 h-7 rounded-full transition-colors ${settings.notifications_enabled ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.notifications_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Volume2 size={20} className="text-primary" />
                  <div>
                    <p className="font-medium text-text-primary">Message Sound</p>
                    <p className="text-sm text-text-secondary">Play sound for new messages</p>
                  </div>
                </div>
                <button
                  data-testid="toggle-sound"
                  onClick={() => toggleSetting('message_sound')}
                  className={`w-12 h-7 rounded-full transition-colors ${settings.message_sound ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.message_sound ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare size={20} className="text-primary" />
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Show Preview</p>
                    <p className="text-sm text-text-secondary">Show message content in notifications</p>
                  </div>
                </div>
                <button
                  data-testid="toggle-preview"
                  onClick={() => toggleSetting('show_preview')}
                  className={`w-12 h-7 rounded-full transition-colors ${settings.show_preview ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.show_preview ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M2 8l2 2-2 2 2 2-2 2"/><path d="M22 8l-2 2 2 2-2 2 2 2"/><path d="M8 18V6"/><path d="M16 18V6"/></svg>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Vibration</p>
                    <p className="text-sm text-text-secondary">Vibrate on new messages</p>
                  </div>
                </div>
                <button
                  data-testid="toggle-vibrate"
                  onClick={() => toggleSetting('message_vibrate')}
                  className={`w-12 h-7 rounded-full transition-colors ${settings.message_vibrate ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.message_vibrate ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Ringtone Selection */}
          <div className={`rounded-3xl p-6 shadow-card border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'}`}>
            <h2 className={`font-heading text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Notification Ringtone</h2>
            <div className="space-y-2">
              {ringtones.map((tone) => (
                <div
                  key={tone.id}
                  className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                    settings.ringtone === tone.id
                      ? 'bg-primary/10 border-2 border-primary'
                      : isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {/* Preview button */}
                  {tone.id !== 'none' && (
                    <button
                      data-testid={`preview-ringtone-${tone.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        playNotificationSound(tone.id);
                      }}
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all hover:scale-110 active:scale-95 ${
                        isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      <Play size={14} className="ml-0.5" />
                    </button>
                  )}
                  {tone.id === 'none' && (
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-400'
                    }`}>
                      <VolumeX size={14} />
                    </div>
                  )}
                  
                  {/* Ringtone name + select */}
                  <button
                    data-testid={`ringtone-${tone.id}`}
                    onClick={() => changeRingtone(tone.id)}
                    className="flex-1 text-left"
                  >
                    <span className={settings.ringtone === tone.id ? 'text-primary font-medium' : isDarkMode ? 'text-white' : 'text-text-primary'}>
                      {tone.name}
                    </span>
                  </button>
                  {settings.ringtone === tone.id && (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Group Notifications */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <h2 className="font-heading text-lg text-text-primary mb-4">Group Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users size={20} className="text-primary" />
                  <div>
                    <p className="font-medium text-text-primary">Group Messages</p>
                    <p className="text-sm text-text-secondary">Notifications for group chats</p>
                  </div>
                </div>
                <button
                  data-testid="toggle-group"
                  onClick={() => toggleSetting('group_notifications')}
                  className={`w-12 h-7 rounded-full transition-colors ${settings.group_notifications ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.group_notifications ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell size={20} className="text-primary" />
                  <div>
                    <p className="font-medium text-text-primary">Mentions Only</p>
                    <p className="text-sm text-text-secondary">Only notify when mentioned</p>
                  </div>
                </div>
                <button
                  data-testid="toggle-mentions"
                  onClick={() => toggleSetting('mention_notifications')}
                  className={`w-12 h-7 rounded-full transition-colors ${settings.mention_notifications ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.mention_notifications ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Muted Chats */}
          {chats.length > 0 && (
            <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
              <h2 className="font-heading text-lg text-text-primary mb-4">Muted Conversations</h2>
              <p className="text-sm text-text-secondary mb-4">Tap a chat to mute/unmute notifications</p>
              <div className="space-y-2">
                {chats.map((chat) => {
                  const isMuted = mutedChats.includes(chat.id);
                  const displayName = chat.chat_type === 'direct'
                    ? chat.other_user?.display_name || 'Unknown'
                    : chat.group_name;
                  
                  return (
                    <button
                      key={chat.id}
                      data-testid={`mute-chat-${chat.id}`}
                      onClick={() => toggleMuteChat(chat.id)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors ${
                        isMuted ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                          {displayName?.charAt(0).toUpperCase()}
                        </div>
                        <span className={isMuted ? 'text-text-secondary' : 'text-text-primary'}>
                          {displayName}
                        </span>
                      </div>
                      {isMuted ? (
                        <BellOff size={20} className="text-text-tertiary" />
                      ) : (
                        <Bell size={20} className="text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
